import GeoJSON from "ol/format/GeoJSON";

// Transf. coord  EPSG:3857 → EPSG:4326 (WGS84)
function toWGS84([x, y]) {
  if (typeof x !== "number" || typeof y !== "number") return null;
  const lng = (x / 20037508.34) * 180;
  const lat = (y / 20037508.34) * 180;
  const rlat = (180 / Math.PI) * (2 * Math.atan(Math.exp((lat * Math.PI) / 180)) - Math.PI / 2);
  return [lng, rlat];
}


function unwrap(value) {
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return value;
  }
}

// bulid prop. POI
function buildProperties(poi) {
  const style = poi.style || {};
  return {
    id: poi.id,
    name: poi.name,
    color: style.line_color || poi.color || "#0000ff",
    width: style.line_thickness || poi.width || 2,
    icon: poi.icon || null,
    icon_id: poi.icon_id || poi.icon || null,
    fill_color: style.fill_color || poi.fill_color || "#ff0000",
    line_color: style.line_color || poi.line_color || "#0000ff",
    fill_opacity: style.fill_opacity ?? poi.fill_opacity ?? 0.5,
    height: poi.height || 80,
  };
}

export default function convertPoisToGeoJSON(pois) {
  const geojson = new GeoJSON();

  const features = pois
    .map((poi) => {
      if (poi.feature) {
        const feature = geojson.writeFeatureObject(poi.feature, {
          featureProjection: "EPSG:3857",
          dataProjection: "EPSG:4326",
        });
        feature.properties = buildProperties(poi);
        return feature;
      }

      try {
        const geom = unwrap(poi.geom);
        let coords = null;

        if (Array.isArray(geom)) {
          coords = geom;
        } else if (geom && Array.isArray(geom.coordinates)) {
          coords = geom.coordinates;
        }

        let type = geom?.type;
        if (!type && Array.isArray(coords)) {
          if (
            coords.length > 0 &&
            Array.isArray(coords[0]) &&
            coords[0].length > 0 &&
            Array.isArray(coords[0][0])
          ) {
            const depth3 = Array.isArray(coords[0][0][0]);
            if (depth3 && typeof coords[0][0][0][0] === "number") {
              type = "MultiPolygon";
            } else if (!depth3 && typeof coords[0][0][0] === "number") {
              type = "MultiLineString";
            } else {
              type = "Polygon"; 
            }
          } else if (Array.isArray(coords[0]) && typeof coords[0][0] === "number") {
            type = "LineString";
          } else if (coords.length === 2 && typeof coords[0] === "number") {
            type = "Point";
          }
        }



        if (!type || !coords) throw new Error("No valid geometry type");

        if (type === "Point") {
          const converted = toWGS84(coords);
          if (!converted) throw new Error("No valid coord. Point");
          return {
            type: "Feature",
            geometry: {type: "Point", coordinates: converted},
            properties: buildProperties(poi),
          };
        }

        if (type === "LineString") {
          const converted = coords.map(toWGS84).filter(Boolean);
          return {
            type: "Feature",
            geometry: {type: "LineString", coordinates: converted},
            properties: buildProperties(poi),
          };
        }

        if (type === "MultiLineString") {
          const converted = coords.map((line) => line.map(toWGS84).filter(Boolean));
          return {
            type: "Feature",
            geometry: {type: "MultiLineString", coordinates: converted},
            properties: buildProperties(poi),
          };
        }

        if (type === "Polygon") {
          const converted = coords.map((ring) => ring.map(toWGS84).filter(Boolean));
          return {
            type: "Feature",
            geometry: {type: "Polygon", coordinates: converted},
            properties: buildProperties(poi),
          };
        }

        if (type === "MultiPolygon") {
          const converted = coords
            .map(
              (polygon) =>
                polygon
                  .map((ring) =>
                    ring
                      .map((coord) => {
                        const c = toWGS84(coord);
                        if (!c) console.warn("No valid coord.  MultiPolygon:", coord);
                        return c;
                      })
                      .filter(Boolean),
                  )
                  .filter((ring) => ring.length > 0), 
            )
            .filter((polygon) => polygon.length > 0); 

          if (converted.length === 0) {
            throw new Error("Invalid coordinates in MultiPolygon");
          }

          return {
            type: "Feature",
            geometry: {type: "MultiPolygon", coordinates: converted},
            properties: buildProperties(poi),
          };
        }

        throw new Error(`Unknown geometry type: ${type}`);
      } catch (e) {
        console.warn("Error accessing geometry for POI:", poi.id, e);
        return null;
      }
    })
    .filter(Boolean);

  return {
    type: "FeatureCollection",
    features,
  };
}
